import React, { useEffect, useState, createContext, useContext } from 'react';
export type FeedbackFieldType = 'rating' | 'text' | 'single-select';
export type FeedbackField = {
  id: string;
  type: FeedbackFieldType;
  label: string;
  required: boolean;
  options?: string[];
};
export type FeedbackForm = {
  id: string;
  name: string;
  active: boolean;
  fields: FeedbackField[];
};
export type FeedbackResponse = {
  id: string;
  formId: string;
  customerPhone?: string;
  branchId?: string;
  submittedAt: number;
  answers: Record<string, any>;
};
type Ctx = {
  forms: FeedbackForm[];
  responses: FeedbackResponse[];
  addForm: (form: Omit<FeedbackForm, 'id'>) => void;
  updateForm: (id: string, patch: Partial<FeedbackForm>) => void;
  deleteForm: (id: string) => void;
  addResponse: (response: Omit<FeedbackResponse, 'id' | 'submittedAt'>) => void;
};
const FeedbackContext = createContext<Ctx | null>(null);
const FORMS_KEY = 'mp_feedback_forms';
const RESPONSES_KEY = 'mp_feedback_responses';
const uid = () => Math.random().toString(36).slice(2, 9);
const seedForms: FeedbackForm[] = [{
  id: 'f_default',
  name: 'General Feedback',
  active: true,
  fields: [{
    id: 'field_1',
    type: 'rating',
    label: 'How was your overall experience?',
    required: true
  }, {
    id: 'field_2',
    type: 'single-select',
    label: 'What did you order?',
    required: false,
    options: ['Dine-in', 'Takeout', 'Delivery']
  }, {
    id: 'field_3',
    type: 'text',
    label: 'Any additional comments?',
    required: false
  }]
}];
const seedResponses: FeedbackResponse[] = [{
  id: 'r_1',
  formId: 'f_default',
  submittedAt: Date.now() - 86400000 * 2,
  answers: {
    field_1: 5,
    field_2: 'Dine-in',
    field_3: 'Great service and food!'
  }
}, {
  id: 'r_2',
  formId: 'f_default',
  submittedAt: Date.now() - 86400000,
  answers: {
    field_1: 4,
    field_2: 'Takeout',
    field_3: 'Food was good, but pickup was a bit slow.'
  }
}];
function loadInitial<T>(key: string, fallback: T): T {
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : fallback;
  } catch {
    return fallback;
  }
}
export function FeedbackProvider({
  children
}: {
  children: ReactNode;
}) {
  const [forms, setForms] = useState<FeedbackForm[]>(() => loadInitial(FORMS_KEY, seedForms));
  const [responses, setResponses] = useState<FeedbackResponse[]>(() => loadInitial(RESPONSES_KEY, seedResponses));
  // Persist forms
  useEffect(() => {
    try {
      window.localStorage.setItem(FORMS_KEY, JSON.stringify(forms));
    } catch {
      /* ignore */
    }
  }, [forms]);
  // Persist responses
  useEffect(() => {
    try {
      window.localStorage.setItem(RESPONSES_KEY, JSON.stringify(responses));
    } catch {
      /* ignore */
    }
  }, [responses]);
  // Cross-tab sync
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === FORMS_KEY && e.newValue) {
        try {
          const parsed = JSON.parse(e.newValue);
          if (Array.isArray(parsed)) setForms(parsed);
        } catch {
          /* ignore */
        }
      }
      if (e.key === RESPONSES_KEY && e.newValue) {
        try {
          const parsed = JSON.parse(e.newValue);
          if (Array.isArray(parsed)) setResponses(parsed);
        } catch {
          /* ignore */
        }
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);
  const addForm = (form: Omit<FeedbackForm, 'id'>) => {
    setForms(prev => [...prev, {
      ...form,
      id: `f_${uid()}`
    }]);
  };
  const updateForm = (id: string, patch: Partial<FeedbackForm>) => {
    setForms(prev => prev.map(f => f.id === id ? {
      ...f,
      ...patch
    } : f));
  };
  const deleteForm = (id: string) => {
    setForms(prev => prev.filter(f => f.id !== id));
    // Optionally delete responses for this form, but keeping them is safer
  };
  const addResponse = (response: Omit<FeedbackResponse, 'id' | 'submittedAt'>) => {
    setResponses(prev => [...prev, {
      ...response,
      id: `r_${uid()}`,
      submittedAt: Date.now()
    }]);
  };
  return <FeedbackContext.Provider value={{
    forms,
    responses,
    addForm,
    updateForm,
    deleteForm,
    addResponse
  }} data-id="element-2113">
      {children}
    </FeedbackContext.Provider>;
}
export function useFeedback() {
  const ctx = useContext(FeedbackContext);
  if (!ctx) throw new Error('useFeedback must be used within FeedbackProvider');
  return ctx;
}